import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { isMenuRouteAllowed, firstGrantedPath } from '@/lib/menuPermissions';
import { SidebarProvider, useSidebar } from '@/components/ui/sidebar';
import { AppSidebar } from './AppSidebar';
import { TopNavigation } from './TopNavigation';
import { useIsMobile } from '@/hooks/use-mobile';
import BillingDialog from './FreeTrailSteps/BillingDialog';
import { useCurrentSubscription } from '@/hooks/useCurrentSubscription';
import { TrialBanner } from '../trial/TrialBanner';
import { WabaLockedBanner } from '../WabaLockedBanner';
import { MetaTokenExpiryBanner } from '../MetaTokenExpiryBanner';
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';
import { AccountScopeBoundary } from './AccountScopeBoundary';
import { useAdsSetupStatus } from '@/hooks/useAds';

import { useAuth } from '@/components/AuthProvider';
import { useInboxSoundNotification } from '@/hooks/useInboxSoundNotification';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';
import { useSidebarTour } from '@/hooks/useSidebarTour';
import { OnboardingChecklist } from '../Onboarding/OnboardingChecklist';
import { SubscriptionGuardProvider } from '@/contexts/SubscriptionGuardContext';
import { AiChatProvider } from '@/contexts/AiChatContext';
import { ChatWidget } from './ChatWidget/ChatWidget';
import { GenUIProvider, useGenUI, GenUIChatbox } from '@/components/genui';
import { GenUIProvider as GenUIFullProvider } from '@/contexts/GenUIContext';
import { PushNotificationManager } from '../PushNotificationManager';
import { PWAInstallPrompt } from '../PWAInstallPrompt';
import { WhatsNewModal } from '../WhatsNewModal';
import { setBlockingDialog } from '@/lib/blockingDialogs';
import {
  ONBOARDING_V2_PREFLIGHT_PATH,
} from '@/components/onboarding-v2/onboardingV2Routes';
import { cn } from "@/lib/utils";
import { useMainScrollRestore } from '@/hooks/useMainScrollRestore';
import { isTrialActuallyExpired as checkTrialExpired, isSubscriptionActive, isSubscriptionExpiredOrCancelled } from "@/lib/subscription-utils";
// WhatsApp calling feature disabled
// import { WhatsAppCallDialer } from '../whatsapp/WhatsAppCallDialer';
// import { IncomingCallDialog } from '../whatsapp/IncomingCallDialog';
// import { useWhatsAppCall } from '@/hooks/useWhatsAppCall';
// import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';
// import { Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
// import {
//   Dialog,
//   DialogContent,
//   DialogTrigger,
// } from "@/components/ui/dialog";

/**
 * Immersive social flow builders (Instagram / Facebook) that own the whole
 * viewport, same as the WhatsApp GenUI flow builder. Matches the builder pages
 * `/home/automation/{facebook,instagram}/new` and `.../:id`, but NOT the flow
 * LIST (`/home/automation/instagram`) or the ANALYTICS sub-page
 * (`.../:id/analytics`). Single source of truth so the full-screen check and
 * the initial-sidebar-state check stay in sync.
 */
const SOCIAL_FLOW_BUILDER_RE =
  /^\/home\/automation\/(facebook|instagram)\/(new|[^/]+)$/;

function isSocialFlowBuilderPath(pathname: string): boolean {
  return SOCIAL_FLOW_BUILDER_RE.test(pathname);
}

interface AppShellProps {
  children: React.ReactNode;
}

const AppShellContent: React.FC<AppShellProps> = ({ children }) => {
  const { state, setOpen } = useSidebar();
  const location = useLocation();
  const mainScrollRef = useMainScrollRestore(location.pathname, location.search);
  const isMobile = useIsMobile();
  const initializedPathnameRef = React.useRef<string | null>(null);
  const [showBillingDialog, setShowBillingDialog] = React.useState(false);
  const { user, loading: authLoading } = useAuth();
  const { isOpen: isGenUIOpen } = useGenUI();
  const isGenUIMode = isGenUIOpen;

  // Auto-collapse sidebar when GenUI mode is active
  const prevGenUIMode = React.useRef(isGenUIMode);
  const prevSidebarState = React.useRef<string>(state);
  React.useEffect(() => {
    if (isGenUIMode && !prevGenUIMode.current) {
      // Entering GenUI mode — remember sidebar state and collapse
      prevSidebarState.current = state;
      setOpen(false);
    } else if (!isGenUIMode && prevGenUIMode.current) {
      // Leaving GenUI mode — restore previous sidebar state
      if (prevSidebarState.current === 'expanded') {
        setOpen(true);
      }
    }
    prevGenUIMode.current = isGenUIMode;
  }, [isGenUIMode]);

  // Determine user role and flags
  const roleRaw = (user?.role || '').toString().toUpperCase();
  const isSuperAdmin = !!(user as any)?.is_super_admin;
  const isAgency = !!(user as any)?.is_agency;
  const isRegularUser = !isSuperAdmin && !isAgency;

  // Handle sidebar state when navigating between pages
  // Only update if pathname changes (not when state changes from manual toggle)
  const previousPathnameRef = React.useRef<string>(location.pathname);

  // No secondary menu logic needed


  const organizationId = user?.organization_id;

  // Check current subscription status (only for regular users). Uses the shared
  // hook so this component + AppSidebar + TopNavigation + SubscriptionGuard +
  // SubscriptionGuardContext + BillingDialog all share a single cached fetch.
  const { data: currentSubscription, isLoading: isSubscriptionLoading, isError: isSubscriptionError } = useCurrentSubscription({
    enabled: !!organizationId && !authLoading && isRegularUser,
    staleTime: 5 * 60 * 1000, // AppShell is the outermost owner — keep its longer 5m TTL.
  });

  // Track whether we've already auto-opened billing dialog for this session
  // to avoid re-opening after user dismisses it
  const hasAutoOpenedBillingRef = React.useRef(false);

  // Check if billing dialog should be shown (only for regular users)
  React.useEffect(() => {
    if (!isRegularUser || typeof window === 'undefined' || !organizationId || authLoading) return;

    // First, check if user has a subscription (active, trialing, or pending)
    // If yes, don't show dialog and clear any localStorage flags
    if (currentSubscription !== undefined) {
      if (isSubscriptionActive(currentSubscription)) {
        setShowBillingDialog(false);
        localStorage.removeItem('showBillingDialog');
        hasAutoOpenedBillingRef.current = false;
        return;
      }

      const isMainPage = location.pathname.startsWith('/home') &&
        !location.pathname.startsWith('/home/admin') &&
        !location.pathname.startsWith('/home/agency');

      const isExpiredOrCancelled = isSubscriptionExpiredOrCancelled(currentSubscription);

      if (isExpiredOrCancelled && isMainPage && !showBillingDialog && !hasAutoOpenedBillingRef.current) {
        hasAutoOpenedBillingRef.current = true;
        localStorage.removeItem('showBillingDialog');
        setShowBillingDialog(true);
        return;
      }

      // No subscription at all (null) or localStorage flag set — check localStorage
      const shouldShowFromStorage = localStorage.getItem('showBillingDialog');

      if (shouldShowFromStorage === 'true') {
        localStorage.removeItem('showBillingDialog');
        if (isMainPage && !showBillingDialog) {
          setShowBillingDialog(true);
        }
      }
    }
  }, [location.pathname, currentSubscription, organizationId, showBillingDialog, authLoading, isRegularUser]);

  // On mobile, sidebar is handled by Sheet component, so no margin needed
  // On desktop, always use collapsed width (icon only)
  // If secondary sidebar is visible, add its width (240px) to the margin
  const secondarySidebarWidth = 0;
  const currentSidebarWidth = state === 'expanded' ? 'var(--sidebar-width)' : 'var(--sidebar-width-icon)';
  const marginLeft = isMobile ? 0 : currentSidebarWidth;
  const width = isMobile ? '100%' : `calc(100% - ${currentSidebarWidth})`;

  // Determine layout based on route and user role
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isAgencyRoute = location.pathname.startsWith('/home/agency');
  const isAdminDashboard = location.pathname === '/admin/dashboard';
  const isAgencyDashboard = location.pathname === '/home/agency/dashboard';
  const isRegularDashboard = location.pathname === '/home';
  const isNewInbox = location.pathname === '/home/new-inbox';
  const isGenUIWorkspace = location.pathname === '/home/genui';
  const isWebsiteBuilderEditor = /^\/home\/website-builder\/sites\/[^/]+\/editor\/[^/]+$/.test(location.pathname);

  // Remove top padding on dashboard pages
  const removeTopPadding = isAdminDashboard || isAgencyDashboard || isRegularDashboard;

  // Admin library builder pages (need same full-height treatment as FlowBuilder)
  const isAdminLibraryBuilder = location.pathname === '/admin/library/flow/builder' ||
    location.pathname === '/admin/library/whatsapp-flow/builder';

  // Remove all padding only for FlowBuilder page (creating/editing flows) to prevent page scrolling
  const isFlowBuilderPage = isAdminLibraryBuilder || (isRegularUser && (
    (location.pathname.startsWith('/home/automation/flows/') &&
      (location.pathname.includes('/new') || location.pathname.match(/\/home\/automation\/flows\/[^/]+$/))) ||
    // Instagram / Facebook visual flow builders — same immersive canvas.
    isSocialFlowBuilderPath(location.pathname)
  ));
  const isCompactGenUIPage = isFlowBuilderPage || location.pathname === '/home/flows/builder';

  // Full-screen, chrome-less builder: the /home automation flow builder hides
  // the sidebar, top nav, and banners so the canvas owns the entire viewport
  // (easier drag/drop + node configuration). Admin library builders are
  // deliberately EXCLUDED — they keep the standard chrome.
  const isFullscreenBuilder = isFlowBuilderPage && !isAdminLibraryBuilder;

  // Determine if we are on a Kanban Board detail page
  const isKanbanBoardPage = isRegularUser && (
    (location.pathname.startsWith('/home/boards/') && location.pathname.split('/').length > 3)
    // Deals pipeline board owns its own internal (per-column) scrolling, like the
    // lead Kanban. NOT /home/deals/stages (that's a normal scrollable settings page).
    || location.pathname === '/home/deals'
    // CRM Tickets board — same full-bleed CRM workspace chrome as the Leads
    // board (flush edge-to-edge, own internal scroll) so switching the Leads /
    // Deals / Tickets tabs doesn't shift the layout. NOT /home/tickets/statuses
    // (that's a normal padded settings page), hence the exact-match.
    || location.pathname === '/home/tickets'
  );

  // Social Media Posts workspace — Card-Theme full-bleed (flush under header,
  // no main px-* gutter). Create-global reuses the same manager shell.
  const isSocialPostsWorkspace = isRegularUser && (
    location.pathname === '/home/social-media/posts'
    || location.pathname === '/home/social-media/posts/create-global'
  );

  // Channels inbox (WhatsApp / Instagram / Facebook / AI Agents) — same
  // full-bleed shell as CRM boards: flush under header, no AppShell px-*,
  // main owns height and panes scroll internally. Without this, Inbox fights
  // default main padding with negative margins and double gutters.
  const isChannelsInbox = isRegularUser && location.pathname.startsWith('/home/channels/');

  const isFullBleedWorkspace = isKanbanBoardPage || isSocialPostsWorkspace || isChannelsInbox;

  // Automations / Forms list pages — same Card-Theme chrome as CRM (flush
  // under header, 60px top, no main px-* gutter) but keep <main> as the
  // scroll owner (CRM boards use overflow-hidden + internal column scroll).
  // Do NOT include flow builders (`/home/automation/flows/:id`, `/home/flows/builder`).
  // Flow executions metrics (`…/executions`) ARE included — Card-Theme list chrome.
  const isAutomationsCardThemeScroll = isRegularUser && (
    location.pathname === '/home/automation'
    || location.pathname === '/home/automation/flows'
    || location.pathname === '/home/flows'
    || /\/home\/automation\/flows\/[^/]+\/executions$/.test(location.pathname)
    || /\/home\/automation\/flows\/[^/]+\/analytics$/.test(location.pathname)
    || /\/home\/automation\/facebook\/[^/]+\/analytics$/.test(location.pathname)
  );

  // Data Stores LIST page — same Card-Theme list chrome as Automations (flush
  // under the header, no main px-* gutter, <main> still scrolls).
  //
  // Exact match only. `/home/data-stores/:id` (the detail page) sizes itself
  // with `h-[calc(100vh-80px)]` off the DEFAULT `pt-[80px] sm:pt-[100px]`
  // padding, so a `startsWith` here would shrink its clearance to 60px and
  // leave 20-40px of its content hanging below the viewport.
  const isDataStoreCardThemeScroll =
    isRegularUser && location.pathname === '/home/data-stores';

  // Integrations LIST page — same Card-Theme list chrome as Data Stores (flush
  // under the header, no main px-* gutter, <main> still scrolls).
  //
  // Exact match only. The per-integration detail pages it links to
  // (`/home/shopify`, `/home/razorpay`, `/home/custom-api`, …) are still
  // ordinary padded pages and must keep the default gutter/top padding.
  const isIntegrationsCardThemeScroll =
    isRegularUser && location.pathname === '/home/integrations';

  // Determine if we should use min-h-screen (admin/agency) or h-screen (regular users)
  const useMinHeight = (isAdminRoute && !isAdminLibraryBuilder) || isAgencyRoute || isNewInbox;

  // Enable sound notifications for inbox messages
  useInboxSoundNotification();

  // Update browser tab title with unread conversation count
  const { totalUnreadConversations } = useUnreadMessages();
  React.useEffect(() => {
    const baseTitle = 'WeNext - Enterprise WhatsApp Business Platform';
    document.title = totalUnreadConversations > 0
      ? `(${totalUnreadConversations}) ${baseTitle}`
      : baseTitle;
  }, [totalUnreadConversations]);

  // Check if user has active plan (uses shared helper — accounts for trial_end date)
  const hasActivePlan = isSubscriptionActive(currentSubscription);

  // Trial state — show trial UI when trial_end is set (our trial system, not Razorpay-created)
  const hasTrialDates = currentSubscription?.trial_end && currentSubscription?.trial_start;
  // isTrialing = status is trialing AND trial hasn't actually ended yet
  const isTrialing = currentSubscription?.status === 'trialing' && hasTrialDates && !checkTrialExpired(currentSubscription);
  // isTrialExpired = status is 'expired' with trial dates OR status is still 'trialing' but trial_end is past
  const isTrialExpired = (currentSubscription?.status === 'expired' && hasTrialDates) || checkTrialExpired(currentSubscription);

  // Paid subscription expiry — cancel_at_period_end or already expired without trial dates
  const isPaidExpired = currentSubscription?.status === 'expired' && !hasTrialDates;
  const isPaidExpiring = currentSubscription?.status === 'active' &&
    currentSubscription?.cancel_at_period_end &&
    currentSubscription?.current_period_end;

  // Determine if the expiring/expired subscription is a trial
  const isTrialPlan = !!(isTrialing || isTrialExpired);

  // Calculate days remaining for both trial and paid subscriptions
  const expiryDate = isTrialing && currentSubscription?.trial_end
    ? new Date(currentSubscription.trial_end)
    : isPaidExpiring && currentSubscription?.current_period_end
      ? new Date(currentSubscription.current_period_end)
      : null;
  const daysRemaining = expiryDate
    ? Math.max(0, Math.ceil((expiryDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;

  // Combined expired state (trial or paid)
  const isSubscriptionExpired = isTrialExpired || isPaidExpired;

  // Treat pending/past_due as having an active plan (payment in progress)
  const isPendingOrPastDue = currentSubscription &&
    (currentSubscription.status === 'pending' || currentSubscription.status === 'past_due');

  // No plan at all (never subscribed, or cancelled/none status — NOT pending/past_due)
  const hasNoPlan = !hasActivePlan && !isSubscriptionExpired && !isPendingOrPastDue;

  // `!isSubscriptionError` is load-bearing: a failed refetch with no cached data
  // would otherwise present as `currentSubscription === undefined` → `hasNoPlan`
  // → false-positive "No Active Plan" banner. Don't accuse the user of being
  // unsubscribed when we can't actually confirm their state.
  const showSubscriptionBanner = isRegularUser && !isFullscreenBuilder && !authLoading && !isSubscriptionLoading && !isSubscriptionError && (
    (isTrialing && daysRemaining <= 5) ||
    (isPaidExpiring && daysRemaining <= 5) ||
    isSubscriptionExpired ||
    hasNoPlan
  );

  // Track banner dismissal so layout adjusts when user closes the banner
  const [bannerDismissed, setBannerDismissed] = React.useState(false);

  // WABA lock banner — selectedAccount is null on landing pages / before BA load
  const { selectedAccount } = useBusinessAccountContext();
  const isWabaLocked =
    !!selectedAccount && selectedAccount.lock_status === 'LOCKED';
  const [wabaBannerDismissed, setWabaBannerDismissed] = React.useState(false);
  // Onboarding V2 shows number issues as an in-page card on the dashboard —
  // never as this strip. Always suppress WABA lock on those routes so a locked
  // demo BA does not replace the real setup blocker.
  const isOnboardingV2 = location.pathname.startsWith(ONBOARDING_V2_PREFLIGHT_PATH);
  const showWabaLockedBanner =
    isRegularUser &&
    !isFullscreenBuilder &&
    isWabaLocked &&
    !wabaBannerDismissed &&
    !isOnboardingV2;

  // Meta ads token expiry banner. `useAdsSetupStatus` only fires when there's
  // an authed regular user; admin / agency roles skip the fetch via the
  // `enabled` flag in the hook itself. token_status is null/'HEALTHY' for
  // healthy accounts, 'EXPIRING_SOON' for the 7-day warning window, and
  // 'EXPIRED' for the post-expiry state.
  const { data: adsSetupStatus } = useAdsSetupStatus();
  const adsAccount = adsSetupStatus?.account;
  const adsTokenStatus = adsAccount?.token_status;
  const isAdsTokenAttention =
    adsTokenStatus === 'EXPIRING_SOON' || adsTokenStatus === 'EXPIRED';
  const [adsBannerDismissed, setAdsBannerDismissed] = React.useState(false);
  // Reset the dismissal when the status escalates (EXPIRING_SOON → EXPIRED)
  // so a customer who clicked away on the yellow warning still sees the red
  // post-expiry banner.
  const lastAdsStatusRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (adsTokenStatus && lastAdsStatusRef.current !== adsTokenStatus) {
      setAdsBannerDismissed(false);
      lastAdsStatusRef.current = adsTokenStatus;
    }
  }, [adsTokenStatus]);
  const showAdsTokenExpiryBanner =
    isRegularUser && !isFullscreenBuilder && isAdsTokenAttention && !adsBannerDismissed;

  // Height consumed by the in-flow Meta strips, published to full-height pages
  // as `--app-banner-offset` (see the <main> style block). Each strip is one
  // row: h-8 (32px) content + py-2.5 (20px) + 1px bottom border = 53px.
  //
  // Derived straight from the flags rather than measured with a ResizeObserver:
  // an earlier attempt used one, but the effect had to be declared ABOVE these
  // flags and so could not depend on them, which left it re-running every render
  // and reporting 0. A constant here is simple and correct; if the strip design
  // changes height, update META_STRIP_HEIGHT_PX.
  const META_STRIP_HEIGHT_PX = 53;
  const metaStripsHeight =
    (showWabaLockedBanner ? META_STRIP_HEIGHT_PX : 0) +
    (showAdsTokenExpiryBanner ? META_STRIP_HEIGHT_PX : 0);

  React.useEffect(() => {
    setBlockingDialog('billing', showBillingDialog);
    return () => setBlockingDialog('billing', false);
  }, [showBillingDialog]);

  // Banner height for layout offset (each banner is ~40px when visible)
  const subscriptionBannerHeight = showSubscriptionBanner && !bannerDismissed ? 40 : 0;
  // WABA lock + Meta ads token strips render IN-FLOW at the top of <main>
  // (not fixed). So they do NOT bump `#app-main-scroll` paddingTop — base
  // header clearance stays ~60px (Card-Theme) / ~100px (default), and closing
  // a strip no longer jumps padding 100→60.
  // The WABA banner sits BELOW the top header (less intrusive than the
  // subscription banner, which gates plan access). So only subscription
  // banner contributes to the "above header" offset that pushes the sidebar
  // + header down.
  const bannerHeight = subscriptionBannerHeight;

  // Initialize sidebar tour for logged-in users with active plan
  const { startTour } = useSidebarTour({
    enabled: !authLoading && !!user && isRegularUser && !!hasActivePlan,
    user,
    hasActivePlan: !!hasActivePlan,
    sidebarState: state,
    setSidebarOpen: setOpen,
  });
  const navigate = useNavigate();

  // Menu-permission route enforcement: when an admin has set custom menu
  // permissions for this member, a section that isn't granted must be
  // unreachable by URL — not just hidden in the sidebar. AppShell renders only
  // after ProtectedRoute has waited for the profile, so `menu_permissions` is
  // loaded here. Role gates (RoleRoute) still apply on top of this; this is the
  // per-section gate. Runs for regular users in the /home area only.
  const menuPerms = (user as any)?.menu_permissions;
  const inHomeArea = location.pathname === '/home' || location.pathname.startsWith('/home/');
  if (
    isRegularUser &&
    Array.isArray(menuPerms) &&
    inHomeArea &&
    !isAgencyRoute &&
    !isMenuRouteAllowed(location.pathname, menuPerms)
  ) {
    const target = firstGrantedPath(menuPerms);
    // Skip when target === current to avoid a redirect loop (e.g. a member with
    // no granted sections lands on the dashboard rather than bouncing forever).
    if (target !== location.pathname) {
      return <Navigate to={target} replace />;
    }
  }

  return (
    <div className="w-full h-screen bg-background relative">
      {/* Sidebar - offset by banner height when banner is visible */}
      {!isWebsiteBuilderEditor && !isFullscreenBuilder && (
        <div
          style={{
            marginTop: bannerHeight ? `${bannerHeight}px` : '0',
          }}
        >
          <AppSidebar />
        </div>
      )}

      {/* Top Navigation - Positioned below banner */}
      {!isWebsiteBuilderEditor && !isFullscreenBuilder && (
        <div
          className="fixed right-0 z-50 pointer-events-auto transition-[left,top] duration-200 ease-smooth"
          style={{
            left: marginLeft,
            top: bannerHeight
          }}
        >
          <TopNavigation onStartTour={startTour} onOpenBilling={() => setShowBillingDialog(true)} />
        </div>
      )}

      {/* Meta account strips (WABA lock / ads token expiry) — FIXED directly
          below the top nav, mirroring how TopNavigation itself is positioned.
          They used to render in-flow as the first children of <main>, but <main>
          is `h-screen overflow-y-auto` with `pt-[80px] sm:pt-[100px]`, so a strip
          landed INSIDE that padding: it appeared ~100px down (visible gap above
          it) and, because the full-height inbox below it then overflowed the
          box, the strip was scrolled and clipped at both edges.
          Fixed positioning keeps them flush under the header and out of the
          scroll flow; --app-banner-offset (see <main>'s style) reserves the
          space for full-height pages. */}
      {!isWebsiteBuilderEditor && !isFullscreenBuilder && (showWabaLockedBanner || showAdsTokenExpiryBanner) && (
        <div
          className="fixed right-0 z-40 pointer-events-auto transition-[left,top] duration-200 ease-smooth top-[calc(var(--app-banner-top,0px)+56px)] sm:top-[calc(var(--app-banner-top,0px)+64px)] md:top-[calc(var(--app-banner-top,0px)+62px)]"
          style={{ left: marginLeft, ['--app-banner-top' as any]: `${bannerHeight}px` }}
        >
          {showWabaLockedBanner && (
            <WabaLockedBanner
              accountName={selectedAccount?.name}
              phoneNumber={selectedAccount?.phone_number}
              onDismiss={() => setWabaBannerDismissed(true)}
            />
          )}
          {showAdsTokenExpiryBanner && (
            <MetaTokenExpiryBanner
              tokenStatus={adsTokenStatus as 'EXPIRING_SOON' | 'EXPIRED'}
              tokenExpiry={adsAccount?.token_expiry}
              adAccountName={adsAccount?.ad_account_name}
              onDismiss={() => setAdsBannerDismissed(true)}
            />
          )}
        </div>
      )}

      {/* Main Content */}
      <main
        id="app-main-scroll"
        ref={mainScrollRef}
        className={[
          'bg-gradient-subtle relative z-0 transition-[left,width] duration-200 ease-smooth',
          isWebsiteBuilderEditor
            ? 'h-screen overflow-hidden p-0 m-0'
            : isFlowBuilderPage
              ? 'h-screen overflow-hidden' // Full height, no padding for FlowBuilder page only - FlowBuilder handles its own padding
              : isGenUIWorkspace
                // flex-col so the flex-1 AccountScopeBoundary bridge below can
                // fill remaining height (GenUI pages size with flex/h-full).
                ? 'flex h-screen flex-col overflow-hidden'
                : isFullBleedWorkspace
                  // Full height, no scroll on main, but keep header visible.
                  // flex-col so the flex-1 page bridge fills space below the
                  // FIXED Meta strips reserved via paddingTop + metaStripsHeight.
                  ? 'flex h-screen flex-col overflow-hidden pt-[60px] sm:pt-[60px] mt-[40px] sm:mt-0'
                  : isAutomationsCardThemeScroll || isDataStoreCardThemeScroll || isIntegrationsCardThemeScroll
                    // CRM gutter/top chrome, but main still scrolls (unlike kanban).
                    ? 'h-screen overflow-y-auto scrollbar-hide pt-[60px] sm:pt-[60px] mt-[40px] sm:mt-0'
                    : useMinHeight
                      ? !removeTopPadding
                        ? cn(
                          "min-h-screen pt-[80px] sm:pt-[100px] pb-4 sm:pb-6 lg:pb-8",
                          !isNewInbox && "px-4 sm:px-6 lg:px-8"
                        )
                        : 'min-h-screen pt-[60px] sm:pt-[60px]'
                      : !removeTopPadding
                        ? cn(
                          "h-screen overflow-y-auto scrollbar-hide pt-[80px] sm:pt-[100px] pb-4 sm:pb-6 lg:pb-8 mt-[40px] sm:mt-0",
                          !isNewInbox && "px-4 sm:px-6 lg:px-8"
                        )
                        : 'h-screen overflow-y-auto scrollbar-hide pt-[60px] sm:pt-[60px] mt-[40px] sm:mt-0'
        ].join(' ')}
        style={{
          left: (isWebsiteBuilderEditor || isFullscreenBuilder) ? 0 : marginLeft,
          width: (isWebsiteBuilderEditor || isFullscreenBuilder) ? '100%' : width,
          position: 'absolute',
          top: 0, // Push content down by announcement height
          right: 0,
          // Height consumed by the in-flow Meta strips below. Full-height pages
          // (the inbox is sized `100vh - header`) must account for this or they
          // overflow the viewport and get clipped at the bottom. Exposed as a
          // CSS variable so a page can opt in with
          // `calc(... - var(--app-banner-offset))` without prop drilling; it is
          // 0px whenever no strip is showing, so pages that ignore it are
          // unaffected.
          //
          // MEASURED, not hardcoded: the strips' height depends on their padding,
          // button sizes and whether the message wraps to a second line on narrow
          // viewports. A literal constant silently desynchronises the moment any
          // of that changes (an early version of this used 44px and was ~9px short).
          ['--app-banner-offset' as any]: `${metaStripsHeight}px`,
          // Header clearance only. WABA lock + Meta ads token strips are
          // rendered in-flow below (first children of <main>), so they must
          // NOT inflate paddingTop — that caused Card-Theme pages to jump
          // between 60px and 100px when the lock strip opened/closed.
          // Header clearance + the FIXED Meta strips' height. The strips overlay
          // content (they are position:fixed, see below the top nav), so their
          // height must be reserved here or the first rows of every page sit
          // underneath them. metaStripsHeight is 0 when no strip is showing, so
          // every existing value is unchanged in the common case.
          paddingTop: (isWebsiteBuilderEditor
            ? 0
            : isFlowBuilderPage
              ? 0
              : isGenUIWorkspace
                ? (isMobile ? 60 : 60)
                : isFullBleedWorkspace || isAutomationsCardThemeScroll || isDataStoreCardThemeScroll || isIntegrationsCardThemeScroll
                  // Full-bleed / Automations Card-Theme sit flush under the fixed
                  // top nav (~60px) — no extra buffer. Without this branch the
                  // inline style overrode the className back to 100px.
                  ? (isMobile ? 60 : 60)
                  : (!removeTopPadding
                    ? (isMobile ? 80 : 100)
                    : (isMobile ? 60 : 60))) + metaStripsHeight,
        }}
      >
        {/* Remounts the page subtree + clears URL params/transient storage on
            business-account switch so filters never leak across accounts.
            Shell chrome (sidebar, top nav, banners) is outside the boundary
            and keeps its state.

            Full-bleed / GenUI pages need a flex-1 height bridge so children
            using flex-1 / h-full fill the content box under FIXED Meta-strip
            padding. Do NOT wrap every route — a bare heightless <div> breaks
            FlowBuilder (/home/automation/flows/new uses h-full off <main>;
            AccountScopeBoundary is a Fragment and must stay the direct child). */}
        {(isFullBleedWorkspace || isGenUIWorkspace) ? (
          <div className="flex min-h-0 flex-1 flex-col">
            <AccountScopeBoundary>{children}</AccountScopeBoundary>
          </div>
        ) : (
          <AccountScopeBoundary>{children}</AccountScopeBoundary>
        )}
      </main>

      {/* Subscription Banner - Shows when trial or paid plan is expiring soon or expired */}
      {showSubscriptionBanner && (
        <div className="fixed top-0 left-0 right-0 z-[70]">
          <TrialBanner
            daysRemaining={daysRemaining}
            isExpired={!!isSubscriptionExpired || !!hasNoPlan}
            isTrialPlan={isTrialPlan}
            hasNoPlan={!!hasNoPlan}
            onDismiss={() => setBannerDismissed(true)}
          />
        </div>
      )}

      {/* Subscription expired warning is now handled by AppSidebar dialog — no full-screen blocker */}

      {/* Billing Dialog - Only for regular users */}
      {isRegularUser && (
        <>
          <BillingDialog open={showBillingDialog} onOpenChange={setShowBillingDialog} />
          {/* <OnboardingChecklist /> */}
        </>
      )}

      {/* Echo AI Chat Widget — opened by the "Ask AI" header button */}
      <ChatWidget />

      {/* GenUI Aria AI Assistant */}
      {isCompactGenUIPage && <GenUIChatbox />}

      {/* Push Notifications — silently reconnects token when user is logged in */}
      <PushNotificationManager />

      <PWAInstallPrompt />

      {/* Never on the first-run onboarding routes. AppShell wraps
          /onboarding/dashboard, so without this a brand-new account finishing
          signup got a "What's New" popup — about features from a release that
          predates their account — thrown over the setup plan before they had
          done anything. Same reasoning that removed the NEXA auto-open from
          this exact moment. They still see it on their next visit to /home.

          `isOnboardingV2` is the startsWith flag, so it covers the questions
          and the plan alike. */}
      {isRegularUser && !isOnboardingV2 && <WhatsNewModal />}

      {/* WhatsApp calling feature disabled
      {isRegularUser && (
        <WhatsAppCallingManager />
      )} */}
    </div>
  );
};

// WhatsApp calling feature disabled
// const WhatsAppCallingManager: React.FC = () => {
//   ...
// };

export const AppShell: React.FC<AppShellProps> = ({ children }) => {
  // Calculate initial sidebar state based on current pathname to prevent layout shift
  // Check if we're on a FlowBuilder page synchronously (before first render)
  const getInitialSidebarState = () => {
    if (typeof window === 'undefined') return true;
    const pathname = window.location.pathname;
    const isAdminLibraryBuilder = pathname === '/admin/library/flow/builder' || pathname === '/admin/library/whatsapp-flow/builder';
    const isFlowBuilderPage = isAdminLibraryBuilder || (pathname.startsWith('/home/automation/flows/') &&
      (pathname.includes('/new') || pathname.match(/\/home\/automation\/flows\/[^/]+$/))) ||
      isSocialFlowBuilderPath(pathname);
    // For admin and agency routes, always start expanded (except library builders). For regular users, collapse on FlowBuilder pages
    const isAdminRoute = pathname.startsWith('/admin');
    const isAgencyRoute = pathname.startsWith('/home/agency');
    if ((isAdminRoute && !isAdminLibraryBuilder) || isAgencyRoute) return true;
    return !isFlowBuilderPage; // Collapsed on FlowBuilder pages, expanded elsewhere
  };

  return (
    <SidebarProvider
      defaultOpen={getInitialSidebarState()}
      style={{ ['--sidebar-width' as any]: '280px', ['--sidebar-width-icon' as any]: '56px' }}
    >
      <GenUIFullProvider>
        <AiChatProvider>
          <GenUIProvider>
            <AppShellContent>{children}</AppShellContent>
          </GenUIProvider>
        </AiChatProvider>
      </GenUIFullProvider>
    </SidebarProvider>
  );
};
